import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const BlogBefore30 = () => {
  const hashtags = [
    "#lifelessons",
    "#before30",
    "#selfgrowth",
    "#innerpeace",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>11 Things to Start Doing Before You Turn 30</title>
        <meta
          name="description"
          content="11 meaningful things to start doing before you turn 30 to build peace, self-awareness, and a life that feels truly yours."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          11 Things to Start Doing Before You Turn 30
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog13.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />

        <section className="space-y-4 mb-8">
          <h2 className="text-secondary text-2xl font-semibold">
            Before 30 Isn’t About Pressure — It’s About Peace
          </h2>

          <p>
            If you’re in your 20s, you’ve probably felt that quiet panic — that
            hum in your chest that says, “I’m running out of time.”
          </p>

          <p>
            Everyone around you seems to be doing something “important.”
            <br />
            Someone’s getting married. Someone’s buying a house.
            <br />
            Someone’s launching a startup.
          </p>

          <p>
            And you? You might be sitting there wondering if you’re even on the
            right path.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here’s the truth most people don’t tell you — your 20s aren’t about
            getting it all together.
          </p>

          <p>
            They’re about laying the emotional, mental, and spiritual foundation
            for a life that feels like yours.
          </p>

          <p>
            You don’t have to know where you’re going.
            <br />
            But you do need to know how to take care of your mind, your soul, and
            your peace as you get there.
          </p>

          <p className="text-[#578d48] font-semibold">
            So here are 11 things to start doing before you turn 30 — not to
            “achieve more,” but to feel more alive.
          </p>
        </section>

        <section className="space-y-6 mb-8">
          <div>
            <h3 className="text-primary font-semibold">
              1. Learn to be alone without feeling lonely
            </h3>
            <p>
              Solitude is not a punishment. It’s a mirror.
            </p>
            <p>
              When you can sit quietly without needing to scroll, text, or
              distract yourself — you start hearing what your soul has been
              trying to tell you all along.
            </p>
            <p>
              Take yourself for a walk without your phone.
              <br />
              Sit by your window in silence.
              <br />
              Learn to breathe through the emptiness.
            </p>
            <p className="text-[#578d48]">
              Because one day, you’ll realize — isolation isn’t loneliness.
              <br />
              It’s a sacred space where you finally meet yourself.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. Heal your relationship with money
            </h3>
            <p>
              So many of us grow up believing money is hard, stressful, or
              scarce.
              <br />
              We chase it, fear it, or use it to fill emotional gaps.
            </p>
            <p className="text-[#578d48]">
              Before 30, learn to see money not as your master — but as your
              mirror.
            </p>
            <p>
              How you treat money says a lot about how you treat yourself.
              <br />
              Respect it, but don’t worship it.
              <br />
              Use it, but don’t let it use you.
            </p>
            <p className="text-[#578d48]">
              A peaceful financial life starts not with more income — but with
              calm intentions.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. Choose peace over drama
            </h3>
            <p>
              Every time you’re pulled into gossip, arguments, or emotional
              chaos — pause and ask:
              <br />
              “Is this worth my energy?”
            </p>
            <p>You’ll realize most things aren’t.</p>
            <p>
              Peace is not built by avoiding people — it’s built by
              understanding where your boundaries end and theirs begin.
            </p>
            <p className="text-[#578d48]">
              Before 30, make it your mission to protect your inner calm like
              it’s your most valuable asset. Because it is.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. Move your body to feel good, not look good
            </h3>
            <p>
              Your body is not an enemy to fight with — it’s your lifelong home.
              <br />
              You don’t have to crush workouts or chase a “perfect body.”
            </p>
            <p>
              Instead, find movement that feels alive.
              <br />
              Dance. Stretch. Walk slowly under the morning sun.
            </p>
            <p className="text-[#578d48]">
              Move because it clears your energy, not because it burns calories.
            </p>
            <p>
              When you start seeing your body as a sacred partner — not a
              project to fix — everything changes.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. Make journaling your morning ritual
            </h3>
            <p>
              There’s something deeply spiritual about putting your thoughts on
              paper.
              <br />
              It’s not about writing perfectly — it’s about being honest.
            </p>
            <p>
              Every morning, ask yourself:
              <br />
              “What am I feeling today?”
              <br />
              “What do I need right now?”
            </p>
            <p className="text-[#578d48]">
              Journaling keeps you close to yourself.
              <br />
              And staying close to yourself is one of the greatest gifts you
              can give your future.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. Forgive your parents — they were just figuring it out too
            </h3>
            <p>
              This one’s hard. But it’s necessary.
            </p>
            <p>
              At some point, you realize your parents weren’t superheroes.
              <br />
              They were people — scared, hopeful, imperfect, doing their best
              with what they knew.
            </p>
            <p>
              Forgiveness doesn’t mean forgetting the pain.
            </p>
            <p className="text-[#578d48]">
              It means freeing yourself from the story that you were never
              loved enough.
            </p>
            <p>
              Because once you forgive, you stop carrying what was never yours
              to begin with.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. Learn to say “no” — gracefully but firmly
            </h3>
            <p>
              There will always be people who want more of your time, your
              energy, your emotional bandwidth.
              <br />
              But every “yes” that isn’t true slowly empties your spirit.
            </p>
            <p className="text-[#578d48]">
              “No” is not rude.
              <br />
              It’s sacred self-respect.
            </p>
            <p className="text-[#578d48]">
              Before 30, learn that your energy is currency — spend it where it
              returns peace, not exhaustion.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. Nurture one or two real friendships
            </h3>
            <p>
              You don’t need a hundred friends.
              <br />
              You need one or two souls who get you — really get you.
            </p>
            <p>
              The ones you can call at 2 a.m. without explaining yourself.
              <br />
              The ones who remind you of who you are when you forget.
            </p>
            <p className="text-[#578d48]">
              Find them. Keep them. Water those connections with time,
              honesty, and love.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. Find stillness every day (even for 5 minutes)
            </h3>
            <p>
              Stillness doesn’t mean meditating for hours or escaping to the
              mountains.
              <br />
              It’s about learning to pause — even for a few breaths — and be
              present.
            </p>
            <p>
              Close your eyes. Feel your breath.
              <br />
              Notice the sounds around you.
              <br />
              Let your shoulders relax.
            </p>
            <p className="text-[#578d48]">
              When you practice stillness daily, you train your mind to return
              home — to the now.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              10. Create something — not for profit, but for your soul
            </h3>
            <p>
              It could be singing, writing, cooking, gardening — anything.
              <br />
              We were born to create, not just to consume.
            </p>
            <p className="text-[#578d48]">
              When you create, you connect with something divine — that flow
              state where time disappears and your soul feels alive.
            </p>
            <p>
              Don’t worry about being good.
              <br />
              Just express.
            </p>
            <p>
              Let your creativity remind you that you are more than your to-do
              list.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              11. Stop rushing your life
            </h3>
            <p className="text-[#578d48]">
              There’s a quote that says, “Don’t let the timeline of others make
              you forget the rhythm of your own soul.”
            </p>
            <p>
              By 30, you’ll start realizing that life unfolds in seasons.
              <br />
              Some people bloom early. Some bloom late.
              <br />
              Both are beautiful.
            </p>
            <p>
              Stop comparing your timeline.
              <br />
              Your story is unfolding exactly as it’s meant to.
            </p>
            <p className="text-[#578d48]">
              The sooner you accept that, the more joy you’ll feel in simply
              living it.
            </p>
          </div>
        </section>

        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            A Quiet Reflection
          </h2>
          <p>
            Before you turn 30, you’ll have moments of confusion, heartbreak,
            and doubt.
          </p>
          <p>
            But that’s okay. That’s life inviting you deeper — into
            self-awareness, resilience, and peace.
          </p>
          <p className="text-[#578d48]">
            You don’t need to rush toward success.
            <br />
            You just need to walk toward yourself.
          </p>
          <p>
            Because when you learn to take care of your inner world — your
            outer world begins to transform naturally.
          </p>
          <p className="text-[#578d48]">
            So breathe.
            <br />
            Forgive.
            <br />
            Let go.
            <br />
            And remember — your 20s aren’t a race.
            <br />
            They’re a sacred classroom where you’re learning who you truly
            are.
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default BlogBefore30;
